// Web auth context — email/password JWT + Emergent Google OAuth (hash session).
// Transport matches the shared backend: a bearer JWT stored in localStorage.
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api, setToken } from "@/lib/api";

const AuthCtx = createContext(null);

export const useAuth = () => {
  const c = useContext(AuthCtx);
  if (!c) throw new Error("useAuth must be inside AuthProvider");
  return c;
};

/**
 * Emergent's Google OAuth redirect appends `#session_id=...` to the URL when
 * consent completes. Pull it out and clean the fragment so a refresh doesn't
 * try to re-consume the same session.
 */
function consumeGoogleHashSession() {
  if (typeof window === "undefined") return null;
  const hash = (window.location.hash || "").replace(/^#/, "");
  if (!hash) return null;
  const params = new URLSearchParams(hash);
  const sid = params.get("session_id");
  if (!sid) return null;
  try {
    window.history.replaceState(null, "", window.location.pathname + window.location.search);
  } catch (e) {
    console.debug("Could not clean OAuth hash from URL:", e);
  }
  return sid;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const me = await api("/auth/me");
      setUser(me);
    } catch {
      setUser(null);
      setToken(null);
    }
  }, []);

  const login = useCallback(async (email, password) => {
    const r = await api("/auth/login", {
      method: "POST",
      body: { email, password },
      auth: false,
    });
    setToken(r.token);
    setUser(r.user);
  }, []);

  const register = useCallback(async (email, password, name) => {
    const r = await api("/auth/register", {
      method: "POST",
      body: { email, password, name },
      auth: false,
    });
    setToken(r.token);
    setUser(r.user);
  }, []);

  const loginWithGoogleSession = useCallback(async (sessionId) => {
    const r = await api("/auth/google-session", {
      method: "POST",
      body: { session_id: sessionId },
      auth: false,
    });
    setToken(r.token);
    setUser(r.user);
  }, []);

  const logout = useCallback(async () => {
    try {
      await api("/auth/logout", { method: "POST" });
    } catch (e) {
      console.warn("Logout API call failed; clearing local session anyway:", e);
    }
    setToken(null);
    setUser(null);
  }, []);

  // Boot: (1) if we came back from Google OAuth, consume the #session_id and
  // exchange it FIRST (before any /auth/me check) to avoid a race; else fall
  // back to the stored bearer token.
  useEffect(() => {
    (async () => {
      const sid = consumeGoogleHashSession();
      if (sid) {
        try {
          await loginWithGoogleSession(sid);
        } catch {
          await refresh();
        }
      } else {
        await refresh();
      }
      setLoading(false);
    })();
  }, [refresh, loginWithGoogleSession]);

  const value = useMemo(
    () => ({ user, loading, login, register, loginWithGoogleSession, logout, refresh }),
    [user, loading, login, register, loginWithGoogleSession, logout, refresh],
  );

  return (
    <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>
  );
}
